import type { TranslatedString } from '@interfaces/shared';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Box, Button, Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '../createModelPortfolio/modelPortfolioGroupBy';
import {
  HouseholdIpsComparison,
  type HouseholdIpsComparisonRow,
} from '../householdIpsComparison/householdIpsComparison';
import {
  HouseholdIpsEditableColumn,
  type HouseholdIpsEdits,
  hasGroupBy,
  useHouseholdIpsEditor,
} from '../householdIpsComparison/householdIpsEditableColumn';
import type {
  HouseholdIpsActuals,
  HouseholdIpsFieldConfig,
  HouseholdIpsValues,
} from '../householdIpsComparison/householdIpsFields';
import { IPS_NARRATIVE_FIELDS } from '../householdIpsComparison/householdIpsFields';
import { HouseholdIpsGroupBySelect } from '../householdIpsComparison/householdIpsGroupBySelect';
import {
  ReadOnlyIpsBands,
  ReadOnlyIpsField,
} from '../householdIpsComparison/householdIpsReadOnlyFields';

const CURRENT_TEST_ID = 'review-ips-summary-current';
const PROPOSED_TEST_ID = 'review-ips-summary-proposed';

export type ReviewIpsSummaryOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  continueButton?: TranslatedString;
  enableCancel?: boolean;
  cancelButton?: TranslatedString;
  applyChangesOnContinue?: boolean;
} & Record<string, any>;

export type ReviewIpsSummaryModel = {
  values: HouseholdIpsValues;
  actuals?: HouseholdIpsActuals;
};

export type ReviewIpsSummaryVisualProps = {
  options: ReviewIpsSummaryOptions;
  fieldConfig: HouseholdIpsFieldConfig;
  proposed: ReviewIpsSummaryModel;
  /** Absent when the workflow never learnt what the household had before. */
  current?: ReviewIpsSummaryModel;
  loading?: boolean;
  onContinue: (values: HouseholdIpsValues) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
};

/**
 * The IPS being proposed, beside the one the household has today. The proposed
 * side stays editable so a last change can be made here rather than by walking
 * the workflow back to the editing step.
 */
export const ReviewIpsSummaryVisual = ({
  options,
  fieldConfig,
  proposed,
  current,
  loading = false,
  onContinue,
  onCancel,
}: ReviewIpsSummaryVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const continueLabel = translateBackendString(options?.continueButton);
  // Empty until the fields report, which they do on mount — and never, when
  // the recap has no fields to show. Seeding this with the model would have a
  // recap of nothing hand the whole model back as though it had been approved
  // field by field.
  const [edits, setEdits] = useState<HouseholdIpsEdits>({
    values: {},
    blocked: false,
  });
  // Both sides total their actual weights against the same asset class level,
  // otherwise the two sets of bands would be read against different numbers.
  const [groupBy, setGroupBy] = useState<ModelPortfolioGroupBy>(
    DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  );

  const showsNothing =
    !fieldConfig?.enableAssetClassTargetAllocations &&
    !IPS_NARRATIVE_FIELDS.some(
      ({ optionKey }) => fieldConfig?.[optionKey]?.enabled,
    );

  const editorArgs = {
    options: fieldConfig,
    initialValues: proposed.values,
    actuals: proposed.actuals,
    groupBy,
    onChange: setEdits,
    disabled: loading,
    testIdPrefix: PROPOSED_TEST_ID,
  };

  return (
    <WorkflowCompletionForm
      onSubmit={() => onContinue(edits.values)}
      testId='review-ips-summary-form'
      submitButtonProps={{
        label: continueLabel || t('continue'),
        disabled: loading || edits.blocked,
        dataTestId: 'review-ips-summary-continue-button',
      }}
      buttonFooter={
        options?.enableCancel && (
          <Button
            variant='contained'
            disabled={loading}
            data-testid='review-ips-summary-cancel-button'
            sx={{ mt: 3, mr: 2, textAlign: 'center' }}
            onClick={onCancel}
            color='secondary'
          >
            {translateBackendString(options?.cancelButton)}
          </Button>
        )
      }
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}

      {showsNothing && (
        <Typography
          variant='bodyMedium'
          sx={{ fontWeight: 500 }}
          data-testid='review-ips-summary-empty'
        >
          {t('householdModelPortfolioIps.summaryEmpty')}
        </Typography>
      )}
      {!showsNothing &&
        (current ? (
          <ComparedIps
            editorArgs={editorArgs}
            current={current}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            loading={loading}
          />
        ) : (
          // The step is given the width of two models whether or not there is
          // anything to compare against, so one on its own is held back to the
          // width the rest of the workflow is read at.
          <Box sx={{ maxWidth: '552px' }}>
            <HouseholdIpsEditableColumn
              {...editorArgs}
              onGroupByChange={setGroupBy}
            />
          </Box>
        ))}
    </WorkflowCompletionForm>
  );
};

const ComparedIps = ({
  editorArgs,
  current,
  groupBy,
  onGroupByChange,
  loading,
}: {
  editorArgs: Parameters<typeof useHouseholdIpsEditor>[0];
  current: ReviewIpsSummaryModel;
  groupBy: ModelPortfolioGroupBy;
  onGroupByChange: (groupBy: ModelPortfolioGroupBy) => void;
  loading: boolean;
}) => {
  const fieldConfig = editorArgs.options;
  const editor = useHouseholdIpsEditor({ ...editorArgs, compact: true });

  const rows: HouseholdIpsComparisonRow[] = IPS_NARRATIVE_FIELDS.filter(
    ({ optionKey }) => fieldConfig?.[optionKey]?.enabled,
  ).map((definition) => ({
    key: definition.valueKey,
    current: (
      <ReadOnlyIpsField
        definition={definition}
        options={fieldConfig}
        values={current.values}
        testIdPrefix={CURRENT_TEST_ID}
      />
    ),
    proposed: editor.field(definition),
  }));

  if (editor.hasBands) {
    // Grouping decides what the actual weights are totalled against, and both
    // models are totalled the same way: the side being proposed chooses, and
    // the side the household already has follows, read-only like the rest of
    // that column.
    if (hasGroupBy(fieldConfig, editorArgs.actuals)) {
      rows.push({
        key: 'groupBy',
        current: (
          <HouseholdIpsGroupBySelect
            testId={`${CURRENT_TEST_ID}-group-by-select`}
            value={groupBy}
            onChange={() => undefined}
            disabled
          />
        ),
        proposed: (
          <HouseholdIpsGroupBySelect
            testId={`${PROPOSED_TEST_ID}-group-by-select`}
            value={groupBy}
            onChange={onGroupByChange}
            disabled={loading}
          />
        ),
      });
    }

    rows.push({
      key: 'targetAllocations',
      current: (
        <ReadOnlyIpsBands
          options={fieldConfig}
          values={current.values}
          actuals={current.actuals}
          groupBy={groupBy}
          compact
          testIdPrefix={CURRENT_TEST_ID}
        />
      ),
      proposed: editor.bands(),
    });
  }

  return (
    <HouseholdIpsComparison
      testId='review-ips-summary-comparison'
      rows={rows}
    />
  );
};

export default ReviewIpsSummaryVisual;
